import {requestEditor} from './editor-rpc.mjs';

/**
 * Owned extension boundary for the recovered editor runtime.
 * Call installStudioBridge(require) from a webpack chunk startup callback once
 * the editor dependencies are registered. The runtime stays private to this
 * closure. User commands use native commits, preparation, history, and saving.
 */
const bridgeKey = Symbol.for("local.mockup-studio.bridge.v1");

function viewport(store) {
  const panel = store.getPanels().find((panel) => panel.name === "Viewport");
  if (!panel) throw new Error("Studio viewport is not ready");
  return panel;
}

function findControl(controls, path) {
  for (const control of controls) {
    if (control.path === path) return control;
    const child = findControl(control.children ?? [], path);
    if (child) return child;
    for (const tab of control.tabs ?? []) {
      const child = findControl(tab.children ?? [], path);
      if (child) return child;
    }
  }
  return null;
}

function validateValue(control, value) {
  switch (control.type) {
    case "slider":
      if (typeof value !== "number" || !Number.isFinite(value)) break;
      return Math.max(control.min ?? -Infinity, Math.min(control.max ?? Infinity, value));
    case "toggle":
      if (typeof value === "boolean") return value;
      break;
    case "select":
      if ((control.options ?? []).some((option) =>
        (typeof option === "string" ? option : option.value) === value)) return value;
      break;
    case "color":
    case "text":
      if (typeof value === "string") return value;
      break;
    case "lightpad":
      if (value && Number.isFinite(value.x) && Number.isFinite(value.y)) {
        const clamp = (number) => Math.max(control.min ?? -Infinity,
          Math.min(control.max ?? Infinity, number));
        return { x: clamp(value.x), y: clamp(value.y) };
      }
      break;
    default:
      throw new Error(`Unsupported studio control: ${control.path}`);
  }
  throw new TypeError(`Invalid value for studio control: ${control.path}`);
}

export function createStudioBridge(require, {target = globalThis.window} = {}) {
  const store = require(56199).v;
  const models = require(38266);
  function getState() {
    const panel = viewport(store);
    const committedValues = { ...store.getCommittedValues(panel.id) };
    return {
      panelId: panel.id,
      modelId: models.he(committedValues["devices.mockupModel"]),
      committedValues,
    };
  }

  function commit(patch) {
    if (!patch || typeof patch !== "object" || Array.isArray(patch)) {
      throw new TypeError("Studio patch must be an object keyed by control path");
    }
    const panel = viewport(store);
    const validated = {};
    for (const [path, value] of Object.entries(patch)) {
      if (path === "devices.mockupModel") {
        throw new Error("Use selectModel() to change the model");
      }
      let control = findControl(panel.controls, path);
      if (!control) throw new Error(`Unknown studio control: ${path}`);
      // The slider schema can lag model selection and keep the flat ±60/70
      // range. Native drag orbits a 3D model through ±360 (module 50648).
      if ((path === "camera.tiltX" || path === "camera.tiltY") &&
          store.getValue(panel.id, "devices.mockupModel")) {
        control = { ...control, min: Math.min(control.min ?? -360, -360), max: Math.max(control.max ?? 360, 360) };
      }
      validated[path] = validateValue(control, value);
    }
    if (Object.keys(validated).length) store.commitValues(panel.id, validated);
    return getState();
  }

  async function selectModel(modelId) {
    if (modelId !== "" && !models.aR(modelId)) {
      throw new Error(`Unknown studio model: ${String(modelId)}`);
    }
    const panel = viewport(store);
    const previous = store.getValue(panel.id, "devices.mockupModel");
    await require(17638).T(modelId, () => {
      // Match the native picker, including the foldable model's opening pose.
      if (modelId === "iphoneDuo" && previous !== modelId) {
        store.commitValues(panel.id, {
          "devices.mockupModel": modelId,
          "devices.laptopHingeAngle": 180,
        });
      } else {
        store.commitPath(panel.id, "devices.mockupModel", modelId);
      }
    });
    return getState();
  }

  function subscribe(listener) {
    let stopPanel = () => {};
    let panelId;
    const update = () => {
      const panel = store.getPanels().find((item) => item.name === "Viewport");
      if (panel?.id !== panelId) {
        stopPanel();
        panelId = panel?.id;
        stopPanel = panel ? store.subscribeCommit(panel.id, emit) : () => {};
      }
      emit();
    };
    const emit = () => listener(panelId ? getState() : null);
    const stopList = store.subscribePanelList(update);
    update();
    return () => { stopPanel(); stopList(); };
  }

  return Object.freeze({
    version: 1,
    pauseTimeline: () => requestEditor(target, "pause_timeline"),
    get ready() { return store.getPanels().some((panel) => panel.name === "Viewport"); },
    getState,
    getControls: () => {
      const panel = viewport(store);
      const values = store.getCommittedValues(panel.id);
      const result = [];
      function visit(controls) {
        for (const control of controls) {
          if (control.path) {
            const item = {path: control.path, label: control.label ?? control.path,
              type: control.type, value: values[control.path]};
            for (const key of ["min", "max", "step", "options", "unit"]) {
              if (control[key] !== undefined) item[key] = control[key];
            }
            result.push(item);
          }
          visit(control.children ?? []);
          for (const tab of control.tabs ?? []) visit(tab.children ?? []);
        }
      }
      visit(panel.controls);
      return JSON.parse(JSON.stringify(result));
    },
    getRendererState: () => require(38001).l(getState().committedValues),
    getModels: () => Object.values(models.Ng).map((model) => ({
      id: model.id, label: model.label, url: model.url, free: model.free === true,
    })),
    commit,
    selectModel,
    subscribe,
    createRenderer: (canvas, options) => require(91987).createRenderer(canvas, options),
  });
}

export function installStudioBridge(require, target = window) {
  if (target[bridgeKey]) return target[bridgeKey].bridge;
  const bridge = createStudioBridge(require, {target});
  const emit = (name, detail) => target.dispatchEvent(new target.CustomEvent(name, { detail }));
  const select = async (event) => {
    const detail = event.detail ?? {};
    try {
      const state = await bridge.selectModel(detail.modelId);
      emit("studio:command-complete", { command: "select-model", requestId: detail.requestId, state });
    } catch (error) {
      emit("studio:command-error", {
        command: "select-model", requestId: detail.requestId, message: error.message,
      });
    }
  };
  target.addEventListener("studio:select-model", select);
  const stop = bridge.subscribe((state) => emit("studio:state", state));
  target[bridgeKey] = {
    bridge,
    dispose() {
      stop();
      target.removeEventListener("studio:select-model", select);
      delete target[bridgeKey];
      if (target.MockupStudio === bridge) delete target.MockupStudio;
    },
  };
  target.MockupStudio = bridge;
  emit("studio:bridge-ready", { version: 1, ready: bridge.ready });
  return bridge;
}

export function uninstallStudioBridge(target = window) {
  target[bridgeKey]?.dispose();
}
